import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import * as fallback from '../mock/data';
import snapshot from '../data/site-content.json';

const ContentContext = createContext(null);

// Static snapshot is the primary source - works without any backend on cPanel/Hostinger.
// Dev mode still tries to refresh from the API for live admin updates.
const SNAPSHOT = {
  products: snapshot.products?.length ? snapshot.products : fallback.products,
  testimonials: snapshot.testimonials?.length ? snapshot.testimonials : fallback.testimonials,
  hero_slides: snapshot.hero_slides?.length ? snapshot.hero_slides : fallback.heroSlides,
  gallery: snapshot.gallery?.length ? snapshot.gallery : fallback.galleryImages.map((g) => ({ image: g })),
  company: {
    name: fallback.companyInfo.name,
    tagline: fallback.companyInfo.tagline,
    established: fallback.companyInfo.established,
    phones: fallback.companyInfo.phone,
    emails: fallback.companyInfo.email,
    contacts: fallback.companyInfo.contacts,
    address: fallback.companyInfo.address,
    whatsapp: fallback.companyInfo.whatsapp,
    business_hours: '',
    youtube_id: fallback.companyInfo.youtubeId,
    socials: fallback.companyInfo.socials,
    industries: fallback.industries,
    ...(snapshot.company || {}),
  },
  site_content: snapshot.site_content || {
    home_h1: 'ESS BEE HEATERS PVT. LTD.',
    home_subtitle: 'Leading manufacturer of industrial & domestic heating elements.',
    home_welcome_title: 'Welcome to',
  },
  sections: {
    header_logo_url: 'https://essbeeheaters.com/admin/uploads/logo/1720678855loo.png',
    topbar_welcome: 'Welcome to Ess Bee Heaters Pvt. Ltd.',
    nav_items: [
      { label: 'Home', path: '/' },
      { label: 'About Us', path: '/about' },
      { label: 'Products', path: '/products' },
      { label: 'Gallery', path: '/gallery' },
      { label: 'Contact Us', path: '/contact' },
    ],
    ticker_items: ['ISO 9001:2015 Certified', 'RoHS Compliant', 'CE Certified', 'Made in India'],
    stats_counters: [],
    whychoose_features: [],
    manufacturing_steps: [],
    certifications_items: [],
    whychoose_title: 'Built On Trust & Heat',
    manufacturing_title: 'Our Manufacturing Process',
    certifications_title: 'Our Certifications',
    industries_title: 'Industries We Cater',
    show_ticker: true, show_h1_band: true, show_welcome: true, show_stats: true,
    show_certifications: true, show_manufacturing: true, show_spotlight: true,
    show_whychoose: true, show_products: true, show_testimonials: true,
    show_cta: true, show_industries: true, show_gallery_preview: true,
    color_primary: '#f7941e', color_secondary: '#e67e00',
    color_dark: '#1a1a1a', color_red: '#d32f2f',
    ...(snapshot.sections || {}),
  },
};

export function ContentProvider({ children }) {
  const [content, setContent] = useState(SNAPSHOT);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    // Only try the API when a backend URL is configured (dev/admin builds).
    // In the static cPanel build REACT_APP_BACKEND_URL is empty so we skip this entirely.
    if (!process.env.REACT_APP_BACKEND_URL) return;
    try {
      setLoading(true);
      const { data } = await api.get('/content');
      setContent({
        products: data.products?.length ? data.products : SNAPSHOT.products,
        testimonials: data.testimonials?.length ? data.testimonials : SNAPSHOT.testimonials,
        hero_slides: data.hero_slides?.length ? data.hero_slides : SNAPSHOT.hero_slides,
        gallery: data.gallery?.length ? data.gallery : SNAPSHOT.gallery,
        company: { ...SNAPSHOT.company, ...(data.company || {}) },
        site_content: { ...SNAPSHOT.site_content, ...(data.site_content || {}) },
        sections: { ...SNAPSHOT.sections, ...(data.sections || {}) },
      });
    } catch (e) {
      // Keep snapshot on failure
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <ContentContext.Provider value={{ ...content, loading, refresh }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used within ContentProvider');
  return ctx;
}

// Convenience hook for company info in same shape as old companyInfo
export function useCompany() {
  const { company } = useContent();
  return {
    ...company,
    phone: company.phones || [],
    email: company.emails || [],
    youtubeId: company.youtube_id || '',
  };
}
